import { useQuery } from '@apollo/client';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { referralsQuery } from '../../../../graphql-operations';
import { gqlTypes } from '../../../../types';
import { Box, Container, Paper, Stack, Text } from '@mantine/core';
import { ReactComponent as Gift } from '../../../../assets/images/icons/gift_yellow.svg';
import referralBg from '../../../../assets/images/friend_referral.jpg';
import underline from '../../../../assets/images/icons/underline.svg';
import ReferralCode from '../../../../components/ReferralCode';
import { useMediaQuery } from '@mantine/hooks';

function ReferralPaperDogBackground({ children }: { children: React.ReactNode }) {
  return (
    <Paper
      bg={'black'}
      radius={16}
      style={{
        backgroundImage: `url(${referralBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'right center',
        overflow: 'hidden',
      }}
    >
      {children}
    </Paper>
  );
}

function ReferralBoxGradient({ children }: { children: React.ReactNode }) {
  return (
    <Box
      px={20}
      py={32}
      sx={{
        background: `linear-gradient(90deg, #F1EFD6 5.79%, #F1EFD6 42.94%, rgba(241, 239, 214, 0.91) 66.18%, rgba(255, 255, 255, 0.00) 100%)`,
      }}
    >
      {children}
    </Box>
  );
}

function ReferralGiftIcon() {
  return (
    <Box
      w={42}
      h={42}
      p={8}
      mb={8}
      sx={(theme) => ({
        backgroundColor: theme.colors['fi-yellow'],
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      })}
    >
      <Gift style={{ color: 'black' }} />
    </Box>
  );
}

const useReferralCode = () => {
  const { data } = useQuery<gqlTypes.ECOMMERCE_referrals>(referralsQuery);
  return data?.currentUser.referrals.referralCode;
};

export default function ReferralsSection() {
  const largeScreen = useMediaQuery('(min-width: 1024px)'); // $md-max-width

  const { t } = useTranslation();
  const referralCode = useReferralCode();
  if (!referralCode) {
    return null;
  }

  return (
    <Container px={largeScreen ? 0 : 20} mx="auto">
      <ReferralPaperDogBackground>
        <ReferralBoxGradient>
          <Stack align="left" spacing={10} pr={'45%'}>
            <ReferralGiftIcon />
            <Text fz={20} fw={700} lh={1.2} align="left" color="type-light-primary" sx={{ letterSpacing: '-0.4px' }}>
              {t('refer_a_friend')}
              {','}
              <br />
              {t('get_one_month')}{' '}
              <Box
                component="span"
                sx={{
                  backgroundImage: `url(${underline})`,
                  backgroundfill: 'yellow',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'bottom',
                  backgroundSize: '100%',
                  paddingBottom: 4,
                }}
              >
                {t('free').toUpperCase()}
              </Box>
            </Text>
            <Text fz={14} fw={600} lh={1.4} align="left" color="type-light-primary" sx={{ letterSpacing: '-0.14px' }}>
              {t('refer_a_friend_subtitle')}
            </Text>
            <ReferralCode code={referralCode} source="thank_you_page" />
          </Stack>
        </ReferralBoxGradient>
      </ReferralPaperDogBackground>
    </Container>
  );
}
